import { ApolloClient, gql, NormalizedCacheObject } from '@apollo/client'
import { IncomingMessage, ServerResponse } from 'http'
import type { ParsedUrlQuery } from 'querystring'
import { getTheatricalRelease } from 'src/services/Theatrical'
import { slugs } from '@/constants/slugs'
import { ServerExperiments } from '@/experimentation'
import { ExperimentParamsArray, runExperimentsOnServer } from '@/experimentation/server'
import { TicketsLayout } from '@/layouts/TicketsLayout'
import { NextPageWithLayout } from '@/layouts/types'
import { getGuildStatus } from '@/services/GuildUserService/GuildUserService'
import { getProjectGuildScore } from '@/services/ProjectsService'
import { getTheatricalReleasePromos } from '@/services/Theatrical/contentfulQueries'
import {
  getGiveawaySchedule,
  getUserGuildDiscounts,
  getValidPublicDiscountsForRelease,
  GiveawaySchedule,
  TheatricalReleaseResult,
  UserGuildDiscounts,
  ValidPublicDiscounts,
} from '@/services/Theatrical/queries'
import { TheatricalReleaseRegion } from '@/types/codegen-federation'
import { logger } from '@/utils/logging'
import { getServerSidePropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { loadTranslations } from '@/utils/translate/translate-server'
import { ShowtimesView } from '@/views/TicketCheckoutViews/ShowtimesView'
import { InitializedReservation, initReservation } from '@/views/TicketCheckoutViews/ShowtimesView/initReservation'
import { TheatricalPromotion } from '@/views/TicketCheckoutViews/components/TheatricalPromotionContext/types'
import { pickCurrentPromo } from '@/views/TicketCheckoutViews/components/TheatricalPromotionContext/utils'

const UPSERT_THEATRICAL_USER_PREFERENCES = gql`
  mutation UpsertTheatricalUserPreferences($input: UserPreferenceInput!) {
    upsertTheatricalUserPreferences(input: $input) {
      id
      preferredVenues {
        id
        name
        providerId
      }
    }
  }
`

interface TicketsPageProps {
  theatricalRelease: TheatricalReleaseResult
  theatricalSlug: string
  showOverlayVideoExperiment?: boolean
  isDecrementingDiscount?: boolean
  projectGuildScore: number | null
  experiments?: ServerExperiments
  reservation: InitializedReservation
  locale: string
  publicDiscounts: ValidPublicDiscounts
  guildCodes?: UserGuildDiscounts
  giveawaySchedule?: GiveawaySchedule | null
  promo: TheatricalPromotion | null
  isLoggedIn?: boolean
}

export const TicketsHypebotPage: NextPageWithLayout<TicketsPageProps> = ({
  theatricalRelease,
  theatricalSlug,
  projectGuildScore,
  publicDiscounts,
  guildCodes,
  experiments,
  reservation,
  giveawaySchedule,
  promo,
  isLoggedIn,
}) => {
  return (
    <TicketsLayout
      shouldShowLanguageModal
      isDarkMode={true}
      useDefaultNav
      regions={theatricalRelease?.theatricalReleaseRegions as TheatricalReleaseRegion[]}
      isLoggedIn={isLoggedIn}
    >
      <ShowtimesView
        reservation={reservation}
        theatricalRelease={theatricalRelease}
        theatricalSlug={theatricalSlug}
        projectGuildScore={projectGuildScore}
        experiments={experiments}
        publicDiscounts={publicDiscounts}
        guildCodes={guildCodes}
        giveawaySchedule={giveawaySchedule}
        promo={promo}
        isLoggedIn={isLoggedIn}
      />
    </TicketsLayout>
  )
}
export const getServerSideProps = getServerSidePropsErrorHandler<TicketsPageProps>(
  async ({ federationClient, locale, next404, params, res, req, query, userId }) => {
    const theatricalSlug = params?.slug
    if (!theatricalSlug || Array.isArray(theatricalSlug)) {
      return next404()
    }

    // If we have a preferred_venue_id, process it first
    if (query.preferred_venue_id && userId) {
      await setPreferredVenueFromQueryParams(query, userId, federationClient)

      // Prepare clean URL for redirect
      const { preferred_venue_id, ...cleanedQuery } = query
      const queryString = new URLSearchParams(cleanedQuery as Record<string, string>).toString()

      return {
        redirect: {
          destination: `/${locale}/tickets/${theatricalSlug}${queryString ? `?${queryString}` : ''}`,
          permanent: false,
        },
      }
    }

    const [
      reservation,
      theatricalRelease,
      projectGuildScore,
      publicDiscounts,
      guildStatus,
      guildCodes,
      giveawaySchedule,
      promos,
      translations,
    ] = await Promise.all([
      initReservation({ query, federationClient, theatricalSlug, req, res }),
      getTheatricalRelease({ theatricalSlug, client: federationClient }),
      getProjectGuildScore(theatricalSlug, federationClient),
      getValidPublicDiscountsForRelease(federationClient, theatricalSlug),
      getGuildStatus(federationClient),
      getUserGuildDiscounts(federationClient, userId, theatricalSlug),
      getGiveawaySchedule(federationClient, theatricalSlug),
      getTheatricalReleasePromos({ theatricalSlug }),
      loadTranslations(locale, ['common', 'home', 'tickets', 'chatbot', 'account', 'theatrical-presales']),
    ])

    if (!theatricalRelease) {
      return next404()
    }

    const isPremiumGuildUser = !!guildStatus?.isGuildMember && !!guildStatus?.guildRoles?.hasFreeTickets

    const promo = await pickCurrentPromo({
      promos: promos ?? null,
      query,
      theatricalSlug,
      hasGuildTickets: isPremiumGuildUser ? !!guildCodes?.length : false,
      publicDiscounts,
      giveawaySchedule,
      req,
      res,
    })

    const experiments = await runTicketExperiments({ theatricalSlug, req, res, query, userId, promo })

    return {
      props: {
        ...translations,
        theatricalSlug,
        theatricalRelease,
        reservation,
        locale,
        experiments: experiments ?? null,
        publicDiscounts,
        guildCodes,
        giveawaySchedule,
        promo,
        projectGuildScore: projectGuildScore?.guildScore,
        ...(experiments?.['showtimes-isloggedin-from-server'] ? { isLoggedIn: !!userId } : {}),
      },
    }
  },
)

TicketsHypebotPage.getLayout = (page) => {
  return page
}

export default TicketsHypebotPage

async function runTicketExperiments({
  req,
  res,
  theatricalSlug,
  userId,
  promo,
}: {
  req: IncomingMessage & { cookies: Partial<{ [p: string]: string }> }
  res: ServerResponse
  theatricalSlug: string
  query: ParsedUrlQuery
  userId: string | null
  promo: TheatricalPromotion | null
}): Promise<ServerExperiments> {
  const expConfigs: ExperimentParamsArray = []

  expConfigs.push({ experimentKey: 'ticket_showtime_play_button', defaultValue: false })

  if (theatricalSlug === slugs.david) {
    expConfigs.push({ experimentKey: 'showtimes_opening_weekend', defaultValue: false })
    expConfigs.push({ experimentKey: 'unlimited-inv-share', defaultValue: false })
    if (promo?.code.toUpperCase() === 'KIDSGOFREE') {
      expConfigs.push({ experimentKey: 'david-kidsgofree-promo', defaultValue: false })
    }
    expConfigs.push({ experimentKey: 'david-early-showtimes', defaultValue: false })
    expConfigs.push({ experimentKey: 'tali_progress_bar_v2', defaultValue: false })
  }

  if (!userId) {
    expConfigs.push({ experimentKey: 'preferred_venue_selection_logged_out', defaultValue: false })
  }

  expConfigs.push({ experimentKey: 'showtimes-isloggedin-from-server', defaultValue: false })

  expConfigs.push({ experimentKey: 'showtime-venues-sort', defaultValue: 'premiere' })

  try {
    const experiments = await runExperimentsOnServer(req, res, expConfigs)
    return experiments || {}
  } catch (error) {
    logger().error('Failed to run experiments:', error)
    return expConfigs.reduce((acc, { experimentKey, defaultValue }) => {
      acc[experimentKey] = defaultValue
      return acc
    }, {} as ServerExperiments)
  }
}

const setPreferredVenueFromQueryParams = async (
  query: ParsedUrlQuery,
  userId: string | null,
  federationClient: ApolloClient<NormalizedCacheObject>,
) => {
  if (!userId) return

  if (query?.preferred_venue_id) {
    const input = {
      theaterPreferences: [
        {
          id: query.preferred_venue_id as string,
          priority: 0,
        },
      ],
    }

    try {
      await federationClient.mutate({
        mutation: UPSERT_THEATRICAL_USER_PREFERENCES,
        variables: { input },
      })
    } catch (err) {
      logger().error('Error setting preferred venue:', err)
    }
  }
}
